import classNames from 'classnames';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { PiEnvelopeSimple } from 'react-icons/pi';
import { useNavigate, useParams } from 'react-router-dom';
import { FileInput, Password, Switch } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import DeletePopover from '../../components/buttons/DeletePopover';
import AsyncImg from '../../components/extras/AsyncImg';
import PlaceholderImage from '../../components/extras/PlaceholderImage';
import CustomSelect from '../../components/forms/CustomSelect';
import RequiredInput from '../../components/forms/InputHOC';
import useRolesByUser from '../../hooks/api-calls/useRolesByUser';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { UserService } from '../../services/user/userService';
import { User } from '../../type/entities/user-type';
import cn from '../../utils/classNames';
import FormFooter from '../_layout/_footers/form-footer';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import useFetch from '../../hooks/useFetch';
import { OrganisationService } from '../../services/company/organisationService';
import { validateDNI } from '../../utils/validatorFunctions';
import useRoles from '../../hooks/api-calls/useRoles';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import PhoneInput from '../../components/forms/PhoneInputWrapper';
import { isValidPhoneNumber } from 'react-phone-number-input';

interface CreateFormProps {
  isLoading: boolean;
  submit: Function;
  data?: User | undefined;
  profileImg?: any;
}

const editSchema = yup.object({
  name: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo').required('Campo obligatorio'),
  lastName: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo'),
  email: yup.string().email('Email no válido').required('Campo obligatorio'),
  password: yup.string().min(8, 'Contraseña de al menos 8 caracteres').max(30, 'Contraseña menor de 30 caracteres').matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/, 'La contraseña debe contener al menos una letra mayúscula, una minúscula y un número')
    .when('isCreateMode', {
      is: (val: boolean) => val == true,
      then: (schema: yup.Schema) => schema.required('Campo obligatorio'),
      otherwise: (schema: yup.Schema) => schema.nullable()
    })
    .nullable(),
  passwordConfirm: yup.string()
    .when('password', {
      is: (val: string | null | undefined) => val !== null && val !== undefined && val.length > 0,
      then: (schema: yup.Schema) => schema.oneOf([yup.ref('password'), ''], 'Las contraseñas no coinciden'),
      otherwise: (schema: yup.Schema) => schema.nullable(),
    })
    .when('isCreateMode', {
      is: (val: boolean) => val == true,
      then: (schema: yup.Schema) => schema.required('Campo obligatorio'),
      otherwise: (schema: yup.Schema) => schema.nullable()
    })
    .nullable(),
  profileImg: yup.mixed(),
  roleId: yup.string().required('Campo obligatorio'),
  dni: yup.string().required('Campo obligatorio').test('isValidNif', 'DNI no válido', function (value) {
    if (validateDNI(value)) {
      return true;
    }
    return false;
  }),
  phone: yup.string().notRequired().test("valid-phone", "El teléfono no es válido", (value) => {
        if (value === undefined || value === '' || value === null) {
            return true;
        }
        return isValidPhoneNumber(value)
    }),});


const schema = yup.object({
  name: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo').required('Campo obligatorio'),
  lastName: yup.string().min(1, 'Demasiado corto').max(50, 'Demasiado largo'),
  email: yup.string().email('Email no válido').required('Campo obligatorio'),
  profileImg: yup.mixed(),
  roleId: yup.string().required('Campo obligatorio'),
  dni: yup.string().required('Campo obligatorio').test('isValidNif', 'DNI no válido', function (value) {
    if (validateDNI(value)) {
      return true;
    }
    return false;
  }),
  phone: yup.string().notRequired().test("valid-phone", "El teléfono no es válido", (value) => {
        if (value === undefined || value === '' || value === null) {
            return true;
        }
        return isValidPhoneNumber(value)
    }),

});

const UserForm: React.FC<CreateFormProps> = ({ isLoading, submit, data, profileImg }) => {

  // STATES

  const mode = data ? 'Editar' : 'Crear';
  const [selectedImage, setSelectedImage] = useState<any>(null);
  
  // HOOKS
  
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const service = new UserService();
  const { user } = useSelector((state: RootState) => state.auth);
  const company = user?.companyId;
  const { userCan } = usePrivilege();
  const superAdmin = userCan('list', 'companies');
  const { getRolesList, updateRolesHookFilters } = useRoles({companyId: superAdmin ? null : company});

  // FUNCTIONS
  
  // -----------------------------------------------------------------------------------------------------------
  /**
   * @ES OBTENER COMPAÑIAS
   * @EN GET COMPANIES
   */
  // -----------------------------------------------------------------------------------------------------------
  const [companyData] = useFetch(useCallback(async () => {
    if(!superAdmin) return;
    const companyService = new OrganisationService();
    let filters = {
      limit: 999999,
    };
    const response = await companyService.listOrganisations(filters);
    return response.getResponseData() as User;
  }, [superAdmin]));
  // -----------------------------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------------------------
  /**
   * @ES OBTENER COMPAÑIAS PARA EL SELECT
   * @EN GET COMPANIES FOR THE SELECT
   */
  // -----------------------------------------------------------------------------------------------------------
  const getCompanyOptions = () => {
    let companyOptions = companyData?.data?.map((data: { id: string; name: string; }) => ({
      value: data.id,
      label: data.name,
    }));

    return companyOptions 
    
  }
  // -----------------------------------------------------------------------------------------------------------
  
  // -----------------------------------------------------------------------------------------------------------
  /**
   * @ES MANEJADOR DEL CAMBIO DE IMAGEN DEL USUARIO
   * @EN HANDLER FOR USER IMAGE CHANGE
   * 
   * @param event
   */
  // -----------------------------------------------------------------------------------------------------------
  const handleImageUpload = async (event: React.ChangeEvent<any>) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result);
    };
    reader.readAsDataURL(file);

    try {
      const response = await service.editUserImg(id, file);
      const responseData = response.getResponseData();
      if (responseData.success) {
        setTimeout(() => {
          toast.success('Imagen de perfil actualizada');
        }, 100);
      } else {
        toast.error(responseData.message);
        setSelectedImage(null);
      }
    } catch (error: any) {
      toast.error("Formato de imagen incorrecto");
      setSelectedImage(null);
    }
  };
  // -----------------------------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------------------------
  /**
   * @ES MANEJADOR DEL BORRADO DE IMAGEN DEL USUARIO
   * @EN HANDLER FOR USER IMAGE DELETION
   */
  // -----------------------------------------------------------------------------------------------------------
  const deleteImage = async () => {
    try {
      const response = await service.deleteUserImg(id);
      const responseData = response.getResponseData();
      if (responseData.success) {
        setSelectedImage(null);
        window.location.reload();
      }
    } catch (error: any) {
      toast.error("Error al eliminar la imagen");
    }
  };
  // -----------------------------------------------------------------------------------------------------------

  // FORMIK

  const formik = useFormik({
    initialValues: {
      id: data?.id ?? '',
      company: data?.userRoles[0]?.company?.id ?? '',
      roleId: data?.userRoles[0].role.id ?? '',
      name: data?.name ?? '',
      lastName: data?.lastName ?? '',
      email: data?.email ?? '',
      password: '',
      passwordConfirm: '',
      profileImg: data?.profileImg?.id ?? undefined,
      isCreateMode: mode === 'Crear',
      dni: data?.dni ?? '',
      phone: data?.phone ?? '',
    },
    validationSchema: data ? editSchema : schema,
    validateOnBlur: false,
    onSubmit: values => { submit(values); },
  });

  // USE EFFECTS

  useEffect(() => {
    updateRolesHookFilters({
      filter_filters: {
          companyId: formik.values.company,
      },
    });
  }, [formik.values.company]);


  // RENDER FUNCTIONS

  const getContent = () => {
    return (
      <div className="@container">
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-10 grid gap-4 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11 font-medium">
            <FormGroup
              title="Información General"
              description="Datos principales del usuario"
              className='pt-6'
              titleCols="@md:col-span-2"
              childCols="@md:col-span-10 md:grid-cols-12"
            >
              <div className={cn(mode === 'Crear' ? 'md:col-span-12' : 'md:col-span-8')}>
                <div className='md:grid md:grid-cols-12 gap-4'>
                  <RequiredInput
                    id='name'
                    label="Nombre"
                    className='md:col-span-4'
                    formik={formik}
                  />

                  <RequiredInput
                    id='lastName'
                    label="Apellidos"
                    className='md:col-span-4'
                    formik={formik}
                  />

                  <RequiredInput
                    id='dni'
                    label="DNI"
                    className='md:col-span-4'
                    formik={formik}
                  />

                  <RequiredInput
                    id='email'
                    type="email"
                    label="Correo Electrónico"
                    prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                    className='md:col-span-4'
                    formik={formik}
                  />

                  <PhoneInput
                      id='phone'
                      label='Teléfono'
                      className='md:col-span-4'
                      onBlur={formik.handleBlur}
                      onChange={e => { formik.setFieldValue('phone', e) }}
                      value={formik.values.phone}
                      error={formik.errors.phone}
                      required={false}
                  />

                  { superAdmin && (
                  <CustomSelect
                    required
                    isSearchable
                    id={'company'}
                    label="Compañia"
                    containerClassName='md:col-span-4'
                    value={{ value: formik.values.company, label: getCompanyOptions()?.find((g: any) => g.value === formik.values.company)?.label }}
                    options={getCompanyOptions()}
                    onChange={
                      (e: any) => { formik.setFieldValue('company', e?.value)}
                    }
                    error={formik.errors.company}
                  />
                  )}


                  <CustomSelect
                    required
                    isSearchable
                    id={'roleId'}
                    label="Rol"
                    containerClassName='md:col-span-4'
                    value={{ value: formik.values.roleId, label: getRolesList()?.find((g: any) => g.value === formik.values.roleId)?.label }}
                    options={getRolesList()}
                    onChange={(e: any) => { formik.setFieldValue('roleId', e?.value) }}
                    error={formik.errors.roleId}
                    display = {userCan('list', 'roles')}
                  />

                  {data && (
                    <>
                      <Password
                        id='password'
                        label="Contraseña"
                        className="[&>label>span]:font-medium md:col-span-6"
                        inputClassName="text-sm"
                        onChange={formik.handleChange}
                        value={formik.values.password}
                        error={formik.errors.password}
                      />

                      <Password
                        id='passwordConfirm'
                        label="Confirmar contraseña"
                        className="[&>label>span]:font-medium md:col-span-6"
                        inputClassName="text-sm"
                        onChange={formik.handleChange}
                        value={formik.values.passwordConfirm}
                        error={formik.errors.passwordConfirm}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className={classNames('md:col-span-4', { 'hidden': mode === 'Crear' })}>
                {selectedImage
                  ? <div style={{ height: "230px", width: "200px" }}>
                    <img src={selectedImage} alt="selected" className='mx-auto d-block img-fluid rounded object-cover w-[220px] h-[220px]' />
                  </div>
                  : profileImg
                    ? <AsyncImg id={profileImg.id} isBackground className="mx-auto d-block img-fluid rounded w-[220px] h-[220px] object-cover" />
                    : <PlaceholderImage width={200} height={200} className='mx-auto d-block img-fluid rounded' />
                }
                <div className={'flex justify-start items-center mt-4'}>
                  <FileInput onChange={(e: React.ChangeEvent<any>) => handleImageUpload(e)} autoComplete='photo' placeholder={"Cambiar imagen"} className='me-3' />
                  <DeletePopover
                    title={`Eliminar imagen`}
                    description={`¿Estás seguro de que deseas eliminar la imagen?`}
                    onDelete={deleteImage}
                    size={32}
                    className={classNames({ 'hidden': !profileImg?.id })}
                  />
                </div>
              </div>
            </FormGroup>
          </div>
        </form>
      </div>
    )
  };

  return (
    <>
      <Page container="fluid">
        {(mode === "Editar" && data) && getContent()}
        {mode === "Crear" && getContent()}
      </Page>
      <FormFooter
        submitBtnText={mode + ' Usuario'}
        handleCancelBtn={() => navigate(-1)}
        handleSubmitBtn={formik.submitForm} isLoading={isLoading}
      />
    </>
  )
};

export default UserForm;